import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BankDetails, Breadcrumbs, DeleteModal, Table, UserDetails } from "../components";
import { useAgent, useBank } from "../hooks";

const AgentsPage = () => {
  const tableHeaderColumns = [
    { id: "agent_name", name: "Our Agents" },
    { id: "agent_id", name: "Agent ID" },
    { id: "project_name", name: "Project name" },
    { id: "company_name", name: "Strategic partner name" },
    { id: "total_commission", name: "Total commission" },
    { id: "total_commission_withdrawn", name: "Total commission withdrawn" },
    { id: "total_commission_withdrawable", name: "Total commission withdrawable" },
    {
      id: "total_commission_upcoming_in_future_installments",
      name: "Total Commission upcoming in further installments",
    },
    { id: "action", name: "More Details" },
    { id: "update", name: "Action" },
  ];
  const agentPlotsSoldTableColumns = [
    { id: "project_name", name: "Project Name" },
    { id: "phase_number", name: "Phase Number" },
    { id: "plot_number", name: "Plot Number" },
    { id: "customer_name", name: "Customer Name" },
    { id: "sold_date", name: "Sold Date" },
    { id: "action", name: "More Details" },
  ];

  const [agents, setAgents] = useState([]);
  const [searchParams] = useSearchParams();
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [agentDetails, setAgentDetails] = useState({});
  const [agentBankDetails, setAgentBankDetails] = useState({});
  const [agentPlotsSold, setAgentPlotsSold] = useState({});

  const { getAllAgentsWithCommission, fetchAgentDetails, updateAgent } = useAgent();
  const { updateBankDetails } = useBank();

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) setSelectedAgentId(id);
  }, []);

  useEffect(() => {
    const fetchAllAgents = async () => {
      const { agents, error } = await getAllAgentsWithCommission();
      if (error) return toast.error("Unable to fetch agents data. Please try again");
      setAgents(agents);
    };
    fetchAllAgents();
  }, []);

  const fetchAgentData = async () => {
    const { agent, error } = await fetchAgentDetails(selectedAgentId);
    if (error) return toast.error(error);
    setAgentDetails(agent.agent);
    setAgentBankDetails(agent.bank);
    setAgentPlotsSold(agent.plots_sold);
  };
  useEffect(() => {
    if (selectedAgentId) fetchAgentData();
  }, [selectedAgentId]);

  const handleAgentBankEdit = async (editedData) => {
    const bankId = agentBankDetails.id;
    const dataToUpdate = { ...editedData };
    if (Object.keys(editedData).includes("bank_branch")) {
      dataToUpdate.bank_branch_name = editedData.bank_branch;
    }
    if (Object.keys(editedData).includes("bank_identification")) {
      dataToUpdate.bank_identification_id = editedData.bank_identification;
    }
    const { success, error } = await updateBankDetails(bankId, dataToUpdate);
    if (error) return toast.error(error);
    toast.success(success);
    fetchAgentData();
  };

  const navigate = useNavigate();
  const handleBookingDetailsViewAction = (id) => {
    navigate(`/bookings?id=${id}&agent=${selectedAgentId}`);
  };

  const handleUpdateAgent = async (data) => {
    const { success, error } = await updateAgent(selectedAgentId, data);
    if (error) return toast.error(error);
    toast.success(success);
    await fetchAgentData();
  };

  const handleAgentEdit = (data) => {
    if (!data || Object.keys(data).length === 0) return;
    handleUpdateAgent(data);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleDeleteAgentClick = () => {
    setShowDeleteModal(true);
  };

  const detailsRef = useRef();
  useEffect(() => {
    if (selectedAgentId && detailsRef.current)
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedAgentId]);

  return (
    <>
      <Breadcrumbs />
      <Table
        columns={tableHeaderColumns}
        data={agents}
        title="Agents"
        setSelectedId={setSelectedAgentId}
        handleDelete={handleDeleteAgentClick}
      />
      {selectedAgentId && (
        <main className="section_wrapper" ref={detailsRef}>
          <UserDetails
            parentName="Agent"
            data={agentDetails}
            showEditButton
            title={`${agentDetails.name} Details`}
            handleEdit={handleAgentEdit}
            handleAadharUpload={(id, url) => handleUpdateAgent({ aadhar_document_id: id })}
            handlePanUpload={(id, url) => handleUpdateAgent({ pan_document_id: id })}
          />
          <BankDetails
            parentName="Agent"
            title="Bank Account Details for Commission"
            data={agentBankDetails}
            handleEdit={handleAgentBankEdit}
            handleIdentificationUpload={(imageId, url) => handleAgentBankEdit({ bank_identification_id: imageId })}
            noEdit={true}
          />
          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">Plots sold by {agentDetails.name}</h2>
              <span>Total Plots Sold - {agentPlotsSold.count} plots</span>
            </section>
            <Table
              columns={agentPlotsSoldTableColumns}
              data={agentPlotsSold.plots_sold ?? []}
              handleAction={handleBookingDetailsViewAction}
            />
          </section>
        </main>
      )}
      {showDeleteModal &&
        createPortal(
          <DeleteModal
            title="Delete Agent"
            handleClose={() => setShowDeleteModal(false)}
            message="Are you sure you want to delete this agent?"
            disableDelete
          />,
          document.body
        )}
    </>
  );
};

export default AgentsPage;
